import React, { memo, useCallback } from 'react';
import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useRankNavigationItem } from '@atlassian/jira-navigation-kit-rank-item-service-old/src/services/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages.tsx';

export const PACKAGE_NAME = 'jiraNavigationKitRankItemOld';
export const REORDER_VIEW_EXPERIENCE = { experience: 'reorderView' };

export type MoveItemProps = {
	beforeItemId?: string | null;
	afterItemId?: string | null;
	position: number;
	id: string;
	dataId: string;
	hasSeparator: boolean;
	onClick: () => void;
	isOverflowing?: boolean;
};

const MoveItem = ({
	id,
	dataId,
	beforeItemId,
	afterItemId,
	position,
	hasSeparator,
	onClick,
	isOverflowing,
}: MoveItemProps) => {
	const { formatMessage } = useIntl();
	const [rankNavigationItem] = useRankNavigationItem();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onStartExperience = useExperienceStart({
		...REORDER_VIEW_EXPERIENCE,
		analyticsSource: PACKAGE_NAME,
	});

	const handleMoveAfter = useCallback(() => {
		if (id.includes('optimisticId') || (afterItemId && afterItemId.includes('optimisticId')))
			return;

		onClick();
		onStartExperience();

		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'moveTab', {});

		rankNavigationItem({
			placement: 'after',
			sourceTabIndex: position,
			targetTabIndex: position + 1,
			id,
			afterItemId,
			dataId,
			actionType: 'clicked',
		});
	}, [
		id,
		afterItemId,
		onClick,
		onStartExperience,
		createAnalyticsEvent,
		rankNavigationItem,
		position,
		dataId,
	]);

	const handleMoveBefore = useCallback(() => {
		if (id.includes('optimisticId') || (beforeItemId && beforeItemId.includes('optimisticId')))
			return;

		onClick();
		onStartExperience();
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'moveTab', {});
		rankNavigationItem({
			placement: 'before',
			sourceTabIndex: position,
			targetTabIndex: position - 1,
			id,
			beforeItemId,
			dataId,
			actionType: 'clicked',
		});
	}, [
		id,
		beforeItemId,
		onClick,
		onStartExperience,
		rankNavigationItem,
		position,
		dataId,
		createAnalyticsEvent,
	]);

	if (!beforeItemId && !afterItemId) return null;

	return (
		<DropdownItemGroup hasSeparator={hasSeparator}>
			{beforeItemId && (
				<DropdownItem onClick={handleMoveBefore}>
					{formatMessage(isOverflowing ? messages.moveItemUp : messages.moveItemLeft)}
				</DropdownItem>
			)}
			{afterItemId && (
				<DropdownItem onClick={handleMoveAfter}>
					{formatMessage(isOverflowing ? messages.moveItemDown : messages.moveItemRight)}
				</DropdownItem>
			)}
		</DropdownItemGroup>
	);
};

export default memo(MoveItem);
